import React from 'react';
import { Link as ChakraLink } from '@stockx/ui';
import { isServer } from '@app/utils';
import AppRouterLink from '../Link';
import useHistory from '../../../hooks/useHistory';
import { PageSwitcherTypes } from '../../../data';
import type { LinkProps } from '../types';

interface Props extends LinkProps {
  as?: string;
  className?: string;
  rel?: string;
  prev?: any;
  onClick?: () => void;
}

const SwitcherLink = ({ as: asProp, to, rel, ...props }: Props) => {
  // Ugh, we need to do this, to handle content stack banners
  // They need to provide absolute URLs, so that Android and iOS
  // banners will resolve correctly (eg: launch native apps).
  // Given, it's still an "internal" link to us, yet ReactRouter Link
  // doesn't handle absolute URLs. This 'modifiedInternalLink' strips
  // the absolute path, when it detects it to be internal.
  let modifiedInternalLink: Nullable<string> = null;
  const isToPropString = typeof to === 'string';
  const history = useHistory();

  const isInternalAbsolute = () =>
    isToPropString &&
    asProp !== PageSwitcherTypes.External &&
    to.indexOf('https://') === 0;

  if (isInternalAbsolute() && isToPropString) {
    modifiedInternalLink = to.replace(/^(?:\/\/|[^/]+)*/, '');
  }

  const toDetermined = isToPropString
    ? {
        pathname: modifiedInternalLink || to,
        state: {
          as: asProp,
          prev: props.prev,
        },
      }
    : { ...to, state: { ...to?.state, as: asProp, prev: props?.prev } };

  if (asProp === PageSwitcherTypes.External) {
    const href = isToPropString ? to : to?.pathname;

    return (
      <ChakraLink isExternal href={href} {...props} textDecoration="none">
        {props.children}
      </ChakraLink>
    );
  }

  const handleSearchClick = () => {
    if (props.onClick) {
      props.onClick();
    }

    if (!isServer && toDetermined?.pathname?.includes('/search')) {
      history.push(toDetermined.pathname, toDetermined.state);
    }
  };

  // for search onclick needs to be passed in
  if (!isServer && toDetermined?.pathname?.includes('/search')) {
    return (
      <ChakraLink data-testid="ChakraSwitcherLink" onClick={handleSearchClick}>
        {props.children}
      </ChakraLink>
    );
  }

  return (
    <AppRouterLink
      data-testid="RouterSwitcherLink"
      to={toDetermined}
      rel={rel}
      {...props}
    >
      {props.children}
    </AppRouterLink>
  );
};

export default SwitcherLink;
